import { useEffect, useMemo, useRef, useState } from 'react';
import { Pressable, Text, TextInput, View } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import * as Crypto from 'expo-crypto';
import { useQueryClient } from '@tanstack/react-query';
import Toast from 'react-native-toast-message';
import SheetModal from '@/components/General/SheetModal';
import SelectInput from '@/components/General/inputs/SelectInput';
import DatePickerInput from '@/components/General/inputs/DatePickerInput';
import Callout from '@/components/General/Callout';
import Unicon from '@/components/General/Unicon';
import Toggle from '@/components/General/inputs/Toggle';
import { Mutation } from '@/utils/react-query/Mutations';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import {
    FOLLOW_UP_TOUCH_TYPES,
    FOLLOW_UP_CONNECTION_RESULTS,
    FOLLOW_UP_DISPOSITIONS,
    NOTE_VISIBILITY_OPTIONS,
    followUpResultIsConnected,
} from '@/utils/constants/followUp';

/**
 * CompleteCareTaskSheet — structured completion for FOLLOW_UP care tasks
 * (spec §10.3/§10.4). Reuses LogTouchSheet's field components/constants but
 * submits ONE atomic follow_up_complete_task call — never log_touch plus a
 * separate completion write.
 *
 * OUTREACH requires a touch (type + connection result). OTHER makes the
 * touch optional via an explicit toggle so a plain completion doesn't
 * fabricate a conversation. A client idempotencyKey is generated once per
 * sheet open so a retried submit replays server-side instead of
 * double-completing.
 *
 * Path-step context (§25.7): when the task was generated by a Path step
 * (generationKey 'step:<progressID>'), the step's expected channel
 * prefills Type — never defaulted to phone — while the actual result and
 * receptiveness are never prefilled. A connected result then requires a
 * recorded receptiveness, and Requested No Contact stops the enrollment —
 * both enforced server-side; the client mirrors them for a truthful form.
 */
const CompleteCareTaskSheet = ({ isOpen, onClose, organizationID, taskInstanceID, personID, personName, purpose = 'OUTREACH', enrollmentID = null }) => {
    const touchRequired = purpose === 'OUTREACH';
    const idempotencyKeyRef = useRef(null);
    const queryClient = useQueryClient();

    // Probe the task's context for a Path-step link. The FollowUpTaskContexts
    // row is readable by the task's own assignees; FollowUpStepProgress is
    // team-readable, so non-team helpers detect the step but get no channel
    // prefill — the server still enforces the receptiveness rule.
    const { data: taskContext } = Query.followUp.assignedTask({
        organizationID,
        taskInstanceID,
        options: { enabled: isOpen && !!organizationID && !!taskInstanceID },
    });
    const stepContext = taskContext?.step || null;
    const isPathStep = !!stepContext;
    const expectedChannel = stepContext?.expectedChannel || null;
    const linkedEnrollmentID = enrollmentID || stepContext?.enrollmentID || null;

    const [logTouch, setLogTouch] = useState(touchRequired);
    const [type, setType] = useState('PHONE_CALL');
    const [connectionResult, setConnectionResult] = useState(null);
    const [disposition, setDisposition] = useState(null);
    const [occurredAt, setOccurredAt] = useState(new Date());
    const [noteContent, setNoteContent] = useState('');
    const [noteVisibility, setNoteVisibility] = useState('FOLLOW_UP');

    // Fresh idempotency key + form reset each time the sheet opens. The key
    // survives retries inside one open session — a re-submit after a network
    // failure replays server-side instead of duplicating completion.
    useEffect(() => {
        if (isOpen) {
            idempotencyKeyRef.current = Crypto.randomUUID();
            setLogTouch(touchRequired);
            // Prefill the step's expected channel when known — never the
            // outcome or receptiveness (§25.7).
            setType(expectedChannel || 'PHONE_CALL');
            setConnectionResult(null);
            setDisposition(null);
            setOccurredAt(new Date());
            setNoteContent('');
            setNoteVisibility('FOLLOW_UP');
        } else {
            idempotencyKeyRef.current = null;
        }
    }, [isOpen]);

    const resultOptions = useMemo(
        () => FOLLOW_UP_CONNECTION_RESULTS[type] || [],
        [type]
    );

    const handleTypeChange = (nextType) => {
        setType(nextType);
        if (connectionResult && !FOLLOW_UP_CONNECTION_RESULTS[nextType]?.some((r) => r.value === connectionResult)) {
            setConnectionResult(null);
        }
    };

    const connectedPreview = useMemo(
        () => (connectionResult ? followUpResultIsConnected(type, connectionResult) : null),
        [type, connectionResult]
    );

    const showNoContactWarning = disposition === 'REQUESTED_NO_CONTACT';
    // §25.7 — a connected result on a Path-step task must record the actual
    // receptiveness; a no-answer attempt completes without one.
    const needsDisposition = isPathStep
        && logTouch
        && !!connectionResult
        && followUpResultIsConnected(type, connectionResult)
        && !disposition;

    const completeMutation = Mutation.followUp.completeTask({
        onSuccess: (data) => {
            Toast.show({
                type: 'success',
                text2: data?.touchID
                    ? (data?.isConnected ? 'Task completed — connected' : 'Task completed — attempt recorded')
                    : 'Task completed',
            });
            onClose?.();
        },
        onError: (error) => {
            if (error?.followUpCode === 'FOLLOW_UP_CONFLICT') {
                // Concurrent completion/cancellation — refetch the task
                // views and show the new state, not a failure.
                queryClient.invalidateQueries({ queryKey: QueryKeys.followUp.assignedTask(organizationID, taskInstanceID) });
                queryClient.invalidateQueries({ queryKey: QueryKeys.taskInstances.byId(taskInstanceID) });
                Toast.show({ type: 'info', text2: 'This task was already completed or cancelled.' });
                onClose?.();
                return;
            }
            if (!error?.expected) {
                Toast.show({ type: 'error', text2: 'Failed to complete the task. Please try again.' });
            }
        },
    });

    const submitting = completeMutation.isPending;

    const handleSubmit = () => {
        if (submitting || needsDisposition) return;
        const payload = {
            taskInstanceID,
            idempotencyKey: idempotencyKeyRef.current,
        };

        if (logTouch) {
            if (!type || !connectionResult) return;
            const occurred = occurredAt ? new Date(occurredAt) : new Date();
            // Client guard mirroring the server rule — future "touches"
            // can't masquerade as completed care (spec §9.3).
            if (occurred.getTime() > Date.now() + 5 * 60 * 1000) {
                Toast.show({ type: 'error', text2: "A touch can't be dated in the future — future plans belong in Tasks." });
                return;
            }
            payload.touch = {
                type,
                connectionResult,
                occurredAt: occurred.toISOString(),
            };
            if (disposition) payload.touch.responseDisposition = disposition;
            if (noteContent.trim()) {
                payload.touch.note = { content: noteContent.trim(), visibility: noteVisibility };
            }
        }

        completeMutation.mutate({
            organizationID,
            taskInstanceID,
            personID: personID || taskContext?.personID,
            enrollmentID: linkedEnrollmentID,
            payload,
        });
    };

    const canSubmit = logTouch
        ? (!!type && !!connectionResult && !needsDisposition && !submitting)
        : !submitting;

    return (
        <SheetModal
            isOpen={isOpen}
            title="Complete Task"
            subtitle={personName || undefined}
            cancelText="Cancel"
            confirmText="Complete Task"
            onCancel={onClose}
            onClose={onClose}
            onConfirm={canSubmit ? handleSubmit : null}
            loading={submitting}
        >
            <View style={styles.body}>
                {!touchRequired && (
                    <View style={styles.toggleRow}>
                        <Text style={styles.toggleLabel}>Log a touch with this completion</Text>
                        <Toggle
                            testID="complete-task-log-touch-toggle"
                            value={logTouch}
                            onValueChange={setLogTouch}
                        />
                    </View>
                )}

                {logTouch && (
                    <>
                        <Text style={styles.label}>Type</Text>
                        <View style={styles.chipRow}>
                            {FOLLOW_UP_TOUCH_TYPES.map((t) => {
                                const selected = type === t.value;
                                return (
                                    <Pressable
                                        key={t.value}
                                        testID={`complete-task-type-${t.value}`}
                                        accessibilityRole="button"
                                        accessibilityState={{ selected }}
                                        onPress={() => handleTypeChange(t.value)}
                                        style={[styles.chip, selected && styles.chipSelected]}
                                    >
                                        <Unicon name={t.icon} size={14} colorVariant={selected ? 'primary' : 'textSecondary'} />
                                        <Text style={[styles.chipText, selected && styles.chipTextSelected]}>{t.label}</Text>
                                    </Pressable>
                                );
                            })}
                        </View>

                        <Text style={styles.label}>Result <Text style={styles.required}>*</Text></Text>
                        {!!expectedChannel && type === expectedChannel && (
                            <Text style={styles.attemptHint}>This step expected this channel — change it if the contact happened differently.</Text>
                        )}
                        <View style={styles.chipRow}>
                            {resultOptions.map((r) => {
                                const selected = connectionResult === r.value;
                                return (
                                    <Pressable
                                        key={r.value}
                                        testID={`complete-task-result-${r.value}`}
                                        accessibilityRole="button"
                                        accessibilityState={{ selected }}
                                        onPress={() => setConnectionResult(r.value)}
                                        style={[styles.chip, selected && styles.chipSelected]}
                                    >
                                        <Text style={[styles.chipText, selected && styles.chipTextSelected]}>{r.label}</Text>
                                    </Pressable>
                                );
                            })}
                        </View>
                        {connectedPreview === true && (
                            <Text testID="complete-task-connected-preview" style={styles.connectedHint}>This counts as a connected conversation.</Text>
                        )}
                        {connectedPreview === false && (
                            <Text testID="complete-task-attempt-preview" style={styles.attemptHint}>This counts as an attempt — it won&rsquo;t update the last-connected date.</Text>
                        )}

                        <SelectInput
                            title={isPathStep && connectedPreview === true ? 'Response' : 'Response (optional)'}
                            required={isPathStep && connectedPreview === true}
                            placeholder="How did they respond?"
                            value={disposition}
                            options={FOLLOW_UP_DISPOSITIONS}
                            onUpdate={setDisposition}
                        />
                        {needsDisposition && (
                            <Text style={styles.requiredHint} testID="complete-task-response-required">
                                Record how they responded — a connected result needs an actual response.
                            </Text>
                        )}

                        {showNoContactWarning && (
                            <Callout
                                testID="complete-task-no-contact-warning"
                                type="warning"
                                title="Requested No Contact"
                                message={isPathStep
                                    ? 'This stops the Path for this person immediately and records a contact warning. Team members will see the stop reason until it is resolved.'
                                    : "This records a contact warning on the person's Follow Up profile. Team members will see the warning until a manager resolves it."}
                            />
                        )}

                        <DatePickerInput
                            title="When"
                            mode="datetime"
                            value={occurredAt}
                            onUpdate={(d) => setOccurredAt(d || new Date())}
                            maximumDate={new Date()}
                        />

                        <Text style={styles.label}>Note (optional)</Text>
                        <TextInput
                            testID="complete-task-note-input"
                            style={styles.noteInput}
                            multiline
                            numberOfLines={3}
                            placeholder="Add a note about this touch..."
                            placeholderTextColor="#999"
                            value={noteContent}
                            onChangeText={setNoteContent}
                        />
                        {!!noteContent.trim() && (
                            <SelectInput
                                title="Note visibility"
                                value={noteVisibility}
                                options={NOTE_VISIBILITY_OPTIONS}
                                hideClearButton
                                onUpdate={(v) => setNoteVisibility(v || 'FOLLOW_UP')}
                            />
                        )}
                    </>
                )}

                {!logTouch && (
                    <Text style={styles.simpleHint}>The task will be marked complete without recording a contact result.</Text>
                )}
            </View>
        </SheetModal>
    );
};

const styles = StyleSheet.create(({ colors, fonts }) => ({
    body: { paddingBottom: 16 },
    toggleRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingVertical: 4,
        marginBottom: 4,
    },
    toggleLabel: {
        fontSize: 14,
        fontFamily: fonts.medium,
        color: colors.text,
        flex: 1,
        marginRight: 12,
    },
    label: {
        fontSize: 13,
        fontFamily: fonts.heading,
        color: colors.textSecondary,
        marginTop: 12,
        marginBottom: 6,
    },
    required: { color: colors.error },
    chipRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
    chip: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        paddingHorizontal: 12,
        paddingVertical: 8,
        borderRadius: 16,
        backgroundColor: colors.surface,
        borderWidth: 1,
        borderColor: colors.border,
    },
    chipSelected: {
        backgroundColor: colors.primaryContainer,
        borderColor: colors.primary,
    },
    chipText: {
        fontSize: 13,
        color: colors.textSecondary,
        fontFamily: fonts.medium,
    },
    chipTextSelected: {
        color: colors.primary,
        fontFamily: fonts.bold,
    },
    connectedHint: {
        fontSize: 12,
        color: colors.success,
        marginTop: 6,
        fontFamily: fonts.medium,
    },
    requiredHint: {
        fontSize: 12,
        color: colors.error,
        marginTop: 6,
    },
    attemptHint: {
        fontSize: 12,
        color: colors.textSecondary,
        marginTop: 6,
    },
    simpleHint: {
        fontSize: 13,
        color: colors.textSecondary,
        marginTop: 12,
        lineHeight: 18,
    },
    noteInput: {
        fontSize: 14,
        color: colors.text,
        backgroundColor: colors.inputBackground,
        borderRadius: 10,
        padding: 12,
        textAlignVertical: 'top',
        minHeight: 72,
        _web: { outlineStyle: 'none' },
    },
}));

export default CompleteCareTaskSheet;
